import { tCalendar } from '../../lib/calendar-i18n';

/**
 * 캘린더 에러 → 사용자 안내 (2026-09-15 사용자 지시 · 2026-09-28 main 반영).
 *
 * ⚠ **지금 부르는 곳은 «일정 검색» 하나뿐이다.** 죽은 코드가 아니라 «아직 한 곳만 쓰는» 것이다
 *   — 생성·수정·삭제·RSVP 도 같은 안내를 써야 하고, 그 화면들이 옮겨 올 때 여기에 이어 붙인다.
 *   ⛔ 「호출자가 하나뿐이니 줄이자」로 읽고 갈래를 지우지 말 것. 갈래를 절반만 남기면
 *     「어떤 실패는 사람 말이고 어떤 실패는 코드」가 되어 지금보다 나쁘다.
 *
 * "어떤 에러인지 · 왜 발생했는지 · 어떻게 해결하는지" 를 세 칸으로 돌려준다. 오류 코드는 사용자에게 보이지 않는다. 예전엔 화면마다
 * 서버 코드와 개발자용 문구를 그대로 붙였다 — 예) "[INVALID_ATTENDEES] 유효하지 않은 참석자 1명
 * (다른 institution 또는 비활성)". 이제 표시 지점은 이 모듈이 만든 값만 쓴다.
 *
 * 판단 순서
 *   1. 회의실 예약 실패(RoomBookingError) — 일정은 이미 저장됐으므로 제목을 따로 둔다.
 *   2. 응답이 없음 — 네트워크 끊김 / 시간 초과.
 *   3. 서버 코드 — 코드별 문구(calendar.error.code.*). 인증·한도·일시 장애는 공통 문구.
 *   4. 서버 스키마 검사(FST_ERR_VALIDATION) — 영어 메시지에서 항목·한도를 읽어 한국어로.
 *   5. HTTP 상태 — 401/403/429/5xx.
 *   6. 모르는 거절 — 서버 문구가 한국어면 사유로 쓰고, 아니면 공통 문구.
 *
 * ⚠ 에러 객체는 axios 인지 검사하지 않고 모양(response·code)으로만 읽는다 — 테스트 mock 과 실제
 *    axios 에러를 같은 길로 처리하기 위해서다.
 */

export type CalendarErrorContext =
  | 'create' | 'edit' | 'delete' | 'move' | 'load'
  | 'room' | 'room_list' | 'room_schedule' | 'search' | 'reminder' | 'calendars' | 'recolor';

export interface FriendlyError {
  /** 무엇이 안 됐는지 — 한 줄 제목. */
  title: string;
  /** 왜 안 됐는지. */
  reason: string;
  /** 어떻게 하면 되는지. */
  action: string;
  /** 분류 코드 — **화면에 보여 주지 않는다**(사용자 지시 2026-09-15). '수정하기' 버튼 같은 분기와 테스트에만 쓴다. */
  code?: string;
}

/** 일정 저장 뒤 회의실 예약만 실패했을 때 — 원래 에러를 감싸 "일정은 저장됐다" 는 사실을 전한다. */
export class RoomBookingError extends Error {
  readonly original: unknown;
  constructor(original: unknown) {
    super('room booking failed');
    this.name = 'RoomBookingError';
    this.original = original;
  }
}

type ClientCheck = 'title_required' | 'time_invalid' | 'end_before_start';

/** 저장 전에 화면이 막는 경우의 안내. */
export function clientError(check: ClientCheck): FriendlyError {
  const base = `calendar.error.client.${check}`;
  return {
    title: tCalendar(`${base}.title`),
    reason: tCalendar(`${base}.reason`),
    action: tCalendar(`${base}.action`),
  };
}

type Generic = 'network' | 'timeout' | 'unauthorized' | 'forbidden' | 'rate_limited' | 'server' | 'rejected' | 'unknown';

/** 코드 자체보다 "상태" 가 설명인 것들 — 공통 문구로 보낸다. */
const GENERIC_BY_CODE: Record<string, Generic> = {
  CALENDAR_TX_FAILED: 'server',
  AUTH_INFRA_UNAVAILABLE: 'server',
  RATE_LIMITED: 'rate_limited',
  TOKEN_MISSING: 'unauthorized',
  TOKEN_INVALID: 'unauthorized',
  FORBIDDEN: 'forbidden',
};

/** 같은 안내를 쓰는 코드 묶음. */
const CODE_ALIAS: Record<string, string> = {
  OCCURRENCE_AT_REQUIRED: 'OCCURRENCE_AT_NOT_FOUND',
  INVALID_OCCURRENCE_AT: 'OCCURRENCE_AT_NOT_FOUND',
};

/** 회의실 예약에서 "반복 회차가 안 맞는다" 는 같은 뿌리의 코드들. */
const ROOM_ALIAS: Record<string, string> = {
  OCCURRENCE_AT_NOT_FOUND: 'OCCURRENCE',
  NOT_RECURRING_EVENT: 'OCCURRENCE',
  INVALID_OCCURRENCE_AT: 'OCCURRENCE',
  OCCURRENCE_WITHOUT_EVENT: 'OCCURRENCE',
};

/** 없는 키면 tCalendar 는 키 문자열을 그대로 돌려준다 — 그걸로 존재를 가린다. */
function has(key: string): boolean {
  return tCalendar(key) !== key;
}

function generic(title: string, kind: Generic, code?: string): FriendlyError {
  return {
    title,
    reason: tCalendar(`calendar.error.generic.${kind}.reason`),
    action: tCalendar(`calendar.error.generic.${kind}.action`),
    ...(code ? { code } : {}),
  };
}

function fromCodeEntry(slug: string, ctxTitle: string, code: string): FriendlyError | null {
  const base = `calendar.error.code.${slug}`;
  if (!has(`${base}.reason`)) return null;
  return {
    title: has(`${base}.title`) ? tCalendar(`${base}.title`) : ctxTitle,
    reason: tCalendar(`${base}.reason`),
    action: tCalendar(`${base}.action`),
    code,
  };
}

/** INVALID_RECURRENCE 는 서버가 원인을 메시지에 담는다 — 알려진 경우만 골라 구체 문구로. */
function recurrenceSlug(message: string): string {
  if (message.startsWith('RECURRENCE_COUNT_UNTIL_BOTH')) return 'RECURRENCE_COUNT_UNTIL_BOTH';
  if (message.startsWith('RECURRENCE_UNTIL_INVALID')) return 'RECURRENCE_UNTIL_INVALID';
  if (message.includes('생기는 일정이 없')) return 'RECURRENCE_NO_OCCURRENCE';
  // ⭐ 2026-10-13 — 현지 새벽의 못 적는 「첫째 주 요일」 조합(chat-server localizedRuleTokens).
  if (message.startsWith('RECURRENCE_LOCAL_UNSUPPORTED')) return 'RECURRENCE_LOCAL_UNSUPPORTED';
  return 'INVALID_RECURRENCE';
}

/** Fastify(ajv) 영어 메시지 — "body/title must NOT have more than 200 characters" 등. */
function fromValidation(message: string): FriendlyError {
  const title = tCalendar('calendar.error.validation.title');
  const code = 'FST_ERR_VALIDATION';
  const m = /body\/(?:[\w]+\/)*?(title|description|location|attendeeUserIds|attachments|byday)\b.*?more than (\d+) (characters|items)/.exec(message);
  if (m) {
    const field = tCalendar(`calendar.error.field.${m[1]}`);
    const kind = m[3] === 'characters' ? 'too_long' : 'too_many';
    return {
      title,
      reason: tCalendar(`calendar.error.validation.${kind}.reason`, { field, limit: m[2]! }),
      action: tCalendar(`calendar.error.validation.${kind}.action`, { field }),
      code,
    };
  }
  return {
    title,
    reason: tCalendar('calendar.error.validation.generic.reason'),
    action: tCalendar('calendar.error.validation.generic.action'),
    code,
  };
}

const HANGUL = /[가-힣]/;

export function describeCalendarError(err: unknown, ctx: CalendarErrorContext): FriendlyError {
  if (err instanceof RoomBookingError) return describeCalendarError(err.original, 'room');

  const ctxTitle = tCalendar(`calendar.error.ctx.${ctx}`);
  const e = (err ?? {}) as {
    response?: { status?: number; data?: { code?: unknown; message?: unknown } };
    code?: unknown; message?: unknown; isAxiosError?: boolean;
  };
  const res = e.response;

  // 응답 자체가 없음 — 네트워크 끊김이나 시간 초과.
  if (!res) {
    const netCode = typeof e.code === 'string' ? e.code : '';
    if (netCode === 'ECONNABORTED' || netCode === 'ETIMEDOUT') return generic(ctxTitle, 'timeout', 'TIMEOUT');
    if (e.isAxiosError || netCode === 'ERR_NETWORK') return generic(ctxTitle, 'network', 'NETWORK');
    const msg = typeof e.message === 'string' ? e.message : '';
    // createEvent/patchEvent 가 서버의 빈 응답을 받았을 때 던지는 에러(api/calendar.ts).
    if (msg.includes('returned null event')) {
      return {
        title: tCalendar('calendar.error.save_unconfirmed.title'),
        reason: tCalendar('calendar.error.save_unconfirmed.reason'),
        action: tCalendar('calendar.error.save_unconfirmed.action'),
        code: 'NULL_EVENT',
      };
    }
    return generic(ctxTitle, 'unknown');
  }

  const status = typeof res.status === 'number' ? res.status : undefined;
  const code = typeof res.data?.code === 'string' ? res.data.code : undefined;
  const serverMsg = typeof res.data?.message === 'string' ? res.data.message : '';

  if (code && GENERIC_BY_CODE[code]) return generic(ctxTitle, GENERIC_BY_CODE[code]!, code);
  if (code === 'FST_ERR_VALIDATION') return fromValidation(serverMsg);

  // 회의실 예약 — 제목은 "일정은 저장했지만…" 고정, 사유·해결만 코드별.
  if (ctx === 'room' && code) {
    const slug = ROOM_ALIAS[code] ?? code;
    if (has(`calendar.error.room.${slug}.reason`)) {
      return {
        title: ctxTitle,
        reason: tCalendar(`calendar.error.room.${slug}.reason`),
        action: tCalendar(`calendar.error.room.${slug}.action`),
        code,
      };
    }
  }

  if (code) {
    const slug = code === 'INVALID_RECURRENCE' ? recurrenceSlug(serverMsg) : (CODE_ALIAS[code] ?? code);
    const entry = fromCodeEntry(slug, ctxTitle, code);
    if (entry) return entry;
  }
  // 알림 추가의 409 는 코드가 빠져도 "같은 알림" 이다(기존 판정 유지).
  if (ctx === 'reminder' && status === 409) {
    const entry = fromCodeEntry('REMINDER_DUPLICATE', ctxTitle, code ?? 'REMINDER_DUPLICATE');
    if (entry) return entry;
  }

  const statusCode = code ?? (status ? `HTTP_${status}` : undefined);
  if (status === 401) return generic(ctxTitle, 'unauthorized', statusCode);
  if (status === 403) return generic(ctxTitle, 'forbidden', statusCode);
  if (status === 429) return generic(ctxTitle, 'rate_limited', statusCode);
  if (status !== undefined && status >= 500) return generic(ctxTitle, 'server', statusCode);

  // 모르는 거절 — 서버가 사람 말(한국어)로 이유를 줬으면 그걸 사유로 쓴다.
  if (HANGUL.test(serverMsg)) {
    return {
      title: ctxTitle,
      reason: serverMsg,
      action: tCalendar(ctx === 'room' ? 'calendar.error.room.default.action' : 'calendar.error.generic.rejected.action'),
      ...(statusCode ? { code: statusCode } : {}),
    };
  }
  return generic(ctxTitle, 'rejected', statusCode);
}

/** 팝업(layerAlert)처럼 글자만 받는 곳에 넣을 본문 — 제목은 팝업 제목으로 따로 넘긴다. 오류 코드는 넣지 않는다. */
export function friendlyErrorBody(fe: FriendlyError): string {
  const lines = [
    `${tCalendar('calendar.error.label.reason')}: ${fe.reason}`,
    `${tCalendar('calendar.error.label.action')}: ${fe.action}`,
  ];
  return lines.join('\n');
}
